/**
 * Migrate JSX component syntax to remark-directive syntax.
 *
 * Conversions:
 *   <CostEstimate data="..." />  →  ::costEstimate{data="..."}
 *   <Timeline data="..." />      →  ::timeline{data="..."}
 *   <Mermaid data="..." />       →  ::mermaid{data="..."}
 *
 * Self-closing tags with or without data attribute are handled.
 * The function is idempotent — already-migrated content passes through unchanged.
 */

interface JsxMapping {
    /** JSX component name (PascalCase) */
    jsxName: string;
    /** Directive name (camelCase) */
    directiveName: string;
}

const JSX_MAPPINGS: JsxMapping[] = [
    { jsxName: 'CostEstimate', directiveName: 'costEstimate' },
    { jsxName: 'Timeline', directiveName: 'timeline' },
    { jsxName: 'Mermaid', directiveName: 'mermaid' }
];

export function migrateJsxToDirective(markdown: string): string {
    let result = markdown;

    for (const { jsxName, directiveName } of JSX_MAPPINGS) {
        // Match <ComponentName data="..." /> (with data attribute)
        const withDataPattern = new RegExp(
            `<${jsxName}\\s+data="([^"]*)"\\s*/>`,
            'g'
        );
        result = result.replace(
            withDataPattern,
            `::${directiveName}{data="$1"}`
        );

        // Match <ComponentName /> (without data attribute)
        const withoutDataPattern = new RegExp(
            `<${jsxName}\\s*/>`,
            'g'
        );
        result = result.replace(
            withoutDataPattern,
            `::${directiveName}`
        );
    }

    return result;
}

/**
 * Returns true if the markdown contains any JSX component syntax that
 * would be migrated by migrateJsxToDirective().
 */
export function hasJsxBlockSyntax(markdown: string): boolean {
    return JSX_MAPPINGS.some(({ jsxName }) => {
        const pattern = new RegExp(`<${jsxName}[\\s/>]`);
        return pattern.test(markdown);
    });
}
